import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@vaiu/ui/button';
import { clientKeys } from '@/config/clientKeys';

interface GoogleSignInProps {
  onSuccess: (token: string) => void;
  onError: (error: string) => void;
  disabled?: boolean;
  className?: string;
}

declare global {
  interface Window {
    google: any;
    googleSignInCallback: (response: any) => void;
  }
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess, onError, disabled = false, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isWebView, setIsWebView] = useState(false);
  const [scriptLoadFailed, setScriptLoadFailed] = useState(false);
  const googleClientId = clientKeys.GOOGLE_CLIENT_ID;

  // Detect WebView environment
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isInWebView = /wv|WebView/i.test(userAgent) || 
                       (window as any).VaiuNFCBridge !== undefined ||
                       !(window as any).chrome;
    
    setIsWebView(isInWebView);
    console.log('WebView detected:', isInWebView, 'User agent:', userAgent);
  }, []);

  // Handle Google OAuth callback (id_token in URL hash)
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if we have an id_token in the URL hash (from Google redirect)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const idToken = hashParams.get('id_token');
      const error = hashParams.get('error');

      if (error) {
        console.error('Google OAuth error:', error);
        onError(`Google Sign-In failed: ${error}`);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      if (idToken) {
        try {
          // Verify nonce if stored
          const storedNonce = sessionStorage.getItem('google_oauth_nonce');
          sessionStorage.removeItem('google_oauth_nonce');

          // Send the id_token to backend (same as credential from GSI)
          const loginResponse = await fetch('/api/v1/auth/google', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              credential: idToken, // Backend expects 'credential' field
            }),
          });

          if (!loginResponse.ok) {
            throw new Error('Google authentication failed on server');
          }

          const loginData = await loginResponse.json();
          
          if (loginData.token) {
            // Store token immediately to ensure it's available
            localStorage.setItem('authToken', loginData.token);
            
            // Call onSuccess callback
            onSuccess(loginData.token);
            
            // Clean up URL immediately
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Force page reload to ensure auth state is properly initialized
            // This is especially important in WebView where state might not update correctly
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 100);
          } else {
            throw new Error('No authentication token received from server');
          }
        } catch (err: any) {
          console.error('Google OAuth callback error:', err);
          onError(err.message || 'Google Sign-In failed');
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };

    handleOAuthCallback();
  }, [onSuccess, onError]);

  useEffect(() => {
    // Don't reload script if already loaded
    if (isScriptLoaded || !googleClientId) return;

    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      setIsScriptLoaded(true);
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      setIsScriptLoaded(true);
      setScriptLoadFailed(false);
    };

    script.onerror = () => {
      console.error('Failed to load Google Identity Services script');
      setScriptLoadFailed(true);
      // Don't call onError immediately - show fallback button instead
    };

    // Cleanup function
    return () => {
      // Don't remove script on unmount as it might be used by other components
    };
  }, [googleClientId, isScriptLoaded]);

  useEffect(() => {
    // Initialize Google Sign-In only once when script is loaded
    if (!isScriptLoaded || isInitialized || !window.google || !googleClientId || !buttonRef.current || !containerRef.current) {
      return;
    }

    // Check if this is the real Google API or our stub
    const isRealGoogleApi = window.google?.accounts?.id?.initialize && 
                           !window.google.accounts.id.initialize.toString().includes('stub');

    if (!isRealGoogleApi) {
      console.debug('Waiting for real Google Sign-In API to load...');
      return;
    }

    let rafId = 0;

    const renderGoogleButton = () => {
      const container = containerRef.current;
      const target = buttonRef.current;
      if (!container || !target) return false;

      const width = Math.floor(container.getBoundingClientRect().width);
      if (width <= 0) return false;

      target.innerHTML = '';

      try {
        console.log('🔍 Initializing Google Sign-In with:', {
          origin: window.location.origin,
          clientId: googleClientId.substring(0, 20) + '...',
          host: window.location.host,
          width,
        });
        
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        window.google.accounts.id.renderButton(target, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width,
        });

        setIsInitialized(true);
        return true;
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
        onError('Google Sign-In initialization failed');
        return true;
      }
    };

    const tryRender = () => {
      if (!renderGoogleButton()) {
        rafId = requestAnimationFrame(tryRender);
      }
    };

    tryRender();
      
    // Add global error handler for postMessage errors
    const handleWindowError = (event: ErrorEvent) => {
      if (event.message && event.message.includes('postMessage')) {
        console.error('🚨 Google Sign-In postMessage error:', {
          message: event.message,
          origin: window.location.origin,
          referrer: document.referrer,
          opener: window.opener ? 'exists' : 'null'
        });
      }
    };
    window.addEventListener('error', handleWindowError);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('error', handleWindowError);
    };
  }, [isScriptLoaded, isInitialized, googleClientId, onError]);

  const handleCredentialResponse = async (response: any) => {
    try {
      if (response.credential) {
        // The response.credential contains the JWT token from Google
        // We need to send this to our backend for verification and user creation/login
        console.log('Google Sign-In successful, credential received');
        
        // Send the Google JWT to our platform API for verification
        const loginResponse = await fetch('/api/v1/auth/google', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // client-key should be added by proxy/gateway, not from browser
          },
          body: JSON.stringify({
            credential: response.credential,
          }),
        });

        if (!loginResponse.ok) {
          throw new Error('Google authentication failed on server');
        }

        const loginData = await loginResponse.json();
        
        if (loginData.token) {
          onSuccess(loginData.token);
        } else {
          throw new Error('No authentication token received from server');
        }
      } else {
        throw new Error('No credential received from Google');
      }
    } catch (error: any) {
      console.error('Google Sign-In error:', error);
      onError(error.message || 'Google Sign-In failed');
    }
  };

  // Fallback: Open Google Sign-in URL directly (Android will intercept and use Chrome Custom Tabs)
  const handleWebViewFallback = () => {
    if (!googleClientId) {
      onError('Google Client ID not configured');
      return;
    }

    // Use Google Identity Services redirect flow instead of OAuth2 code flow
    // This returns an id_token (JWT) directly, which matches what the backend expects
    // The redirect URI should be registered in Google Cloud Console as:
    // - https://vaiu.app (or your domain)
    // - https://dev.vaiu.app (for dev)
    const redirectUri = encodeURIComponent(window.location.origin);
    const nonce = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    
    // Store nonce for verification
    sessionStorage.setItem('google_oauth_nonce', nonce);
    
    // Use Google Identity Services endpoint with redirect mode
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${googleClientId}&` +
      `redirect_uri=${redirectUri}&` +
      `response_type=id_token&` +
      `scope=openid%20email%20profile&` +
      `nonce=${nonce}&` +
      `prompt=select_account`;

    console.log('Opening Google Sign-in in Chrome Custom Tabs:', googleAuthUrl);
    console.log('Redirect URI:', window.location.origin);
    
    // Navigate to Google auth URL - Android WebView will intercept and open Chrome Custom Tabs
    window.location.href = googleAuthUrl;
  };

  if (!googleClientId) {
    return (
      <Button variant="outline" disabled className={`w-full h-10 ${className}`}>
        Google Sign-In (Not Configured)
      </Button>
    );
  }

  // Show fallback button if script failed to load (likely WebView)
  if (scriptLoadFailed || (isWebView && !isScriptLoaded)) {
    return (
      <Button 
        variant="outline" 
        className={`w-full h-10 ${className}`}
        onClick={handleWebViewFallback}
        disabled={disabled}
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Sign in with Google
      </Button>
    );
  }

  return (
    <div ref={containerRef} className={`w-full ${className}`}>
      <div 
        ref={buttonRef} 
        className="w-full [&>div]:!w-full [&>div]:!max-w-none [&_iframe]:!w-full [&_iframe]:!max-w-none"
        style={{ 
          opacity: disabled ? 0.5 : 1, 
          pointerEvents: disabled ? 'none' : 'auto' 
        }}
      />
    </div>
  );
};

export default GoogleSignIn; 